import * as React from 'react'
import * as moment from 'moment'

import { GetFirstDashCampaignsSuccessPayload } from 'src/api'
import { CardContent, CardTypes } from '../../molecules/promotional-cards/Card'
import { isEmpty } from 'lodash'
import { DollarBills } from '../../atoms/icons'
import { DEFAULT_PROMOTIONAL_CARD } from './GetStartedPromotions'
import { MonetaryFields } from '@doordash/lib-i18n'

import t from './strings'

/**
 * The lowest unit amount for hourly pay that should be shown to
 * a Dasher.
 * TODO(@anthonylim24): This may need to be adjusted when this feature
 * expands to countries beyond US
 */

const HOURLY_PAY_DISPLAY_LOWER_THRESHOLD = 20 * 100 // 20.00
const HOURLY_PAY_DISPLAY_UPPER_THRESHOLD = 40 * 100 // 40.00

const utcTimeToFormattedTime = (time: string) =>
  moment(time).format('MM/DD h:mm A')

/**
 * Show hourly pay if unitAmount falls beyond an upper and lower bound
 * @param {number} unitAmount the unit amount for pay, in cents
 * @returns {boolean} true to show hourly pay, false to hide
 */
export const shouldShowHourlyPay = (unitAmount: number) => {
  return (
    unitAmount >= HOURLY_PAY_DISPLAY_LOWER_THRESHOLD &&
    unitAmount <= HOURLY_PAY_DISPLAY_UPPER_THRESHOLD
  )
}

/**
 * Takes the API response from getPromotionalCards and maps it to PromotionalCards
 * @param {GetFirstDashCampaignsSuccessPayload[]} firstDashCampaigns - response from getPromotionalCards
 * @param {CardTypes[]} excludeCards - array of card types to exclude from mapping
 * @param {CardContent} altDefaultCard - an alternative default card to display if no payload is mapped
 * @returns {CardContent[]} - used for the PromotionalCards molecule. If the campaigns passed in are empty or contain no valid mappings, a default card will be returned.
 */
export const mapCampaignsToPromotionalCards = (
  firstDashCampaigns: GetFirstDashCampaignsSuccessPayload[],
  excludeCards: CardTypes[] = [],
  altDefaultCard?: CardContent
): CardContent[] => {
  const cards: CardContent[] = []

  if (!isEmpty(firstDashCampaigns)) {
    Object.values(firstDashCampaigns).map(campaign => {
      const pushCard = {
        [CardTypes.PeakPay]: () => {
          if (!excludeCards.includes(CardTypes.PeakPay)) {
            cards.push({
              title: t.promoCardPeakPayTitle(),
              content: t.promoCardPeakPayContent({
                monetaryDisplayString: campaign.payAmount.displayString,
                startDate: utcTimeToFormattedTime(campaign.activationTime),
                endDate: utcTimeToFormattedTime(campaign.activeUntilTime),
              }),
              icon: <DollarBills />,
              type: CardTypes.PeakPay,
            })
          }
        },
        [CardTypes.HourlyPay]: () => {
          if (!excludeCards.includes(CardTypes.HourlyPay)) {
            cards.push({
              title: t.promoCardHourlyPayTitle(),
              content: campaign.extras.submarketName
                ? t.promoCardHourlyPayContentSubmarket({
                    submarketName: campaign.extras.submarketName,
                    monetaryDisplayString: campaign.payAmount.displayString,
                  })
                : t.promoCardHourlyPayContent({
                    monetaryDisplayString: campaign.payAmount.displayString,
                  }),
              icon: <DollarBills />,
              type: CardTypes.HourlyPay,
            })
          }
        },
        [CardTypes.Default]: () => null,
      }

      return (pushCard[campaign.type] || pushCard[CardTypes.Default])()
    })
  }

  // If campaigns passed in have no mappings, push a default card
  if (isEmpty(cards)) {
    cards.push(altDefaultCard || DEFAULT_PROMOTIONAL_CARD)
  }

  return cards
}

export const getHourlyPayFromPromotionalCards = (
  firstDashCampaigns: GetFirstDashCampaignsSuccessPayload[]
): MonetaryFields => {
  if (!isEmpty(firstDashCampaigns)) {
    const hourlyPay = Object.values(firstDashCampaigns).find(
      campaign => campaign.type === CardTypes.HourlyPay
    )

    if (hourlyPay) {
      return hourlyPay.payAmount
    }
  }

  return null
}
